import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { get, ref } from 'firebase/database';
import { database } from '@/contexts/AuthContext';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { Calendar, MapPin, School, Heart, Code } from 'lucide-react';

export default function ProfilePage() {
  const { userId } = useParams();

  const [user, setUser] = useState(null);
  const [userProjects, setUserProjects] = useState([]);
  const [takenProjects, setTakenProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUser();
    fetchUserProjects();
  }, [userId]);

  // Fetch user details
  const fetchUser = async () => {
    try {
      const snapshot = await get(ref(database, `users/${userId}`));
      if (snapshot.exists()) {
        setUser(snapshot.val());
      } else {
        setUser(null);
      }
    } catch (error) {
      toast.error("Failed to load user profile");
    }
  };

  // Fetch all projects
  const fetchUserProjects = async () => {
    try {
      const snapshot = await get(ref(database, "projects"));

      if (snapshot.exists()) {
        const projectsObj = snapshot.val();

        const projectsArray = Object.keys(projectsObj).map(id => ({
          id,
          ...projectsObj[id],
        }));

        // Posted by user
        const posted = projectsArray.filter(p => p.uid === userId);

        // FIXED: Taken by user (taken_by is an OBJECT)
        const taken = projectsArray.filter(p => {
          if (!p.taken_by) return false;

          // Extract UID from nested object
          const takerUid = Object.keys(p.taken_by)[0];

          return takerUid === userId;
        });

        setUserProjects(posted);
        setTakenProjects(taken);
      } else {
        setUserProjects([]);
        setTakenProjects([]);
      }
    } catch (error) {
      console.error("Failed to load projects", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-blue-50">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="glass p-8 rounded-2xl skeleton h-96"></div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-blue-50">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 py-8 text-center">
          <p className="text-gray-500">User not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-blue-50">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Profile Header */}
        <Card className="glass border-white/50 mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">

              <Avatar className="h-32 w-32">
                <AvatarImage src={user.profile_picture} />
                <AvatarFallback className="bg-gradient-to-br from-sky-400 to-cyan-500 text-white text-4xl">
                  {user.full_name?.charAt(0)}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 text-center md:text-left">
                <h1 className="text-3xl font-bold mb-2 break-words">{user.full_name}</h1>
                <p className="text-gray-600 mb-4 break-all">{user.email}</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <School className="w-4 h-4 text-sky-500" />
                    {user.college_name}
                  </div>

                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-sky-500" />
                    {user.district}, {user.state}
                  </div>

                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-sky-500" />
                    {user.created_at}
                  </div>

                  <div className="flex items-center gap-2">
                    <Code className="w-4 h-4 text-sky-500" />
                    {userProjects.length} Projects Posted
                  </div>
                </div>
              </div>

            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">

          <Card className="glass border-white/50">
            <CardContent className="pt-6 text-center">
              <div className="text-4xl font-bold gradient-text">{userProjects.length}</div>
              <div className="text-gray-600">Posted Projects</div>
            </CardContent>
          </Card>

          <Card className="glass border-white/50">
            <CardContent className="pt-6 text-center">
              <div className="text-4xl font-bold gradient-text">{takenProjects.length}</div>
              <div className="text-gray-600">Taken Projects</div>
            </CardContent>
          </Card>

          <Card className="glass border-white/50">
            <CardContent className="pt-6 text-center">
              <div className="text-4xl font-bold gradient-text">
                {userProjects.reduce((acc, p) => acc + Object.keys(p.likes || {}).length, 0)}
              </div>
              <div className="text-gray-600">Total Likes Received</div>
            </CardContent>
          </Card>

        </div>

        {/* Posted Projects */}
        <Card className="glass border-white/50">
          <CardHeader>
            <CardTitle>Posted Projects</CardTitle>
          </CardHeader>

          <CardContent>
            {userProjects.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No posted projects</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {userProjects.map(project => (
                  <div
                    key={project.id}
                    className="p-4 bg-white/70 rounded-xl shadow-md hover:shadow-lg transition cursor-pointer flex flex-col"
                    onClick={() => (window.location.href = `/project/${project.id}`)}
                  >
                    <h3 className="font-semibold text-lg mb-2 line-clamp-2 break-words text-sky-700">
                      {project.title}
                    </h3>

                    <p className="text-gray-600 text-sm mb-3 line-clamp-3">
                      {project.description}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-3">
                      {project.tech_stack?.slice(0, 3).map((tech, idx) => (
                        <Badge key={idx} variant="secondary">{tech}</Badge>
                      ))}

                      {project.tech_stack?.length > 3 && (
                        <Badge variant="secondary">+{project.tech_stack.length - 3}</Badge>
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-auto">
                      <Badge className={
                        project.status === "Completed"
                          ? "bg-green-100 text-green-800"
                          : project.status === "In Development"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-yellow-100 text-yellow-800"
                      }>
                        {project.status}
                      </Badge>

                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Heart className="w-4 h-4" />
                        {Object.keys(project.likes || {}).length}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Taken Projects */}
        <Card className="glass border-white/50 mt-6">
          <CardHeader>
            <CardTitle>Projects Taken</CardTitle>
          </CardHeader>

          <CardContent>
            {takenProjects.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No taken projects</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {takenProjects.map(project => (
                  <div
                    key={project.id}
                    className="p-4 bg-white/70 rounded-xl shadow-md hover:shadow-lg transition cursor-pointer flex flex-col"
                    onClick={() => (window.location.href = `/project/${project.id}`)}
                  >
                    <h3 className="font-semibold text-lg mb-2 line-clamp-2 break-words text-purple-700">
                      {project.title}
                    </h3>

                    <p className="text-gray-600 text-sm mb-3 line-clamp-3">
                      {project.description}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-3">
                      {project.tech_stack?.slice(0, 3).map((tech, idx) => (
                        <Badge key={idx} variant="secondary">{tech}</Badge>
                      ))}

                      {project.tech_stack?.length > 3 && (
                        <Badge variant="secondary">+{project.tech_stack.length - 3}</Badge>
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-auto">
                      <Badge className={
                        project.status === "Completed"
                          ? "bg-green-100 text-green-800"
                          : project.status === "In Development"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-yellow-100 text-yellow-800"
                      }>
                        {project.status}
                      </Badge>

                      
                    </div>

                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
